import { useCallback, useMemo, useState } from "react";
import { useChatStore } from "@/hooks/useChatStore";

export interface UseChatAskUserParams {
  /** Which conversation these prompts belong to. Confirmations are queued per
   *  thread: one turn raises a card per write-tool call, and a card must never
   *  be displaced by another thread's. */
  threadId: string;
  /** The thread is streaming — plain choice buttons stay inert while it is. */
  isLoading: boolean;
  /** Resets the pending answer whenever the conversation moves on. */
  conversationKey: string;
  onSend: (message: string) => void;
}

/**
 * The two mid-stream prompts the agent can raise: AskUserQuestion (pick an
 * option or type your own) and tool confirmations (WHA-3058).
 *
 * Both are chat-store state that only the message list renders, so the wiring
 * lives here rather than in the container.
 */
export function useChatAskUser({
  threadId,
  isLoading,
  conversationKey,
  onSend,
}: UseChatAskUserParams) {
  const chatStore = useChatStore();
  const [pendingChoice, setPendingChoice] = useState<string | null>(null);
  // WHA-3481: free-text answer typed into the AskUserQuestion card. Mutually
  // exclusive with `pendingChoice` — setting one clears the other.
  const [customAnswer, setCustomAnswer] = useState("");

  // WHA-2873: Derive choices from pendingAskUser (mid-stream) if available
  const askUserActive = chatStore.pendingAskUser != null;

  const handleChoiceSelect = useCallback(
    (choice: string) => {
      // Allow selection during loading when AskUserQuestion is active
      if (isLoading && !askUserActive) return;
      setPendingChoice(choice);
      setCustomAnswer("");
    },
    [isLoading, askUserActive]
  );

  // WHA-3481: typing a custom answer deselects any picked option.
  const handleCustomAnswerChange = useCallback((value: string) => {
    setCustomAnswer(value);
    if (value) setPendingChoice(null);
  }, []);

  const handleChoiceConfirm = useCallback(() => {
    // WHA-3481: submit the picked option, or the typed custom answer.
    const answer = pendingChoice ?? (customAnswer.trim() || null);
    if (!answer) return;
    setPendingChoice(null);
    setCustomAnswer("");

    if (askUserActive) {
      // WHA-2873: Submit answer to the SDK's AskUserQuestion tool
      chatStore.submitChoiceAnswer(answer);
      return;
    }
    if (isLoading) return;
    onSend(answer);
  }, [pendingChoice, customAnswer, isLoading, askUserActive, chatStore, onSend]);

  const handleChoiceCancel = useCallback(() => {
    setPendingChoice(null);
    setCustomAnswer("");
  }, []);

  // Reset when the conversation moves on. Adjusting state during render (the
  // documented alternative to a reset effect) keeps the stale answer from ever
  // being painted and avoids a second render pass.
  const [lastConversationKey, setLastConversationKey] = useState(conversationKey);
  if (lastConversationKey !== conversationKey) {
    setLastConversationKey(conversationKey);
    setPendingChoice(null);
    setCustomAnswer("");
  }

  // WHA-2873: Derive choices from pendingAskUser when active
  const askUserChoices = useMemo(() => {
    const pending = chatStore.pendingAskUser;
    if (!pending || !pending.questions[0]) return null;
    const q = pending.questions[0];
    return {
      choices: q.options.map((o) => o.label),
      choiceQuestion: q.question,
    };
  }, [chatStore.pendingAskUser]);

  const confirmations = useToolConfirmations(threadId);

  return {
    pendingChoice,
    customAnswer,
    askUserChoices,
    handleChoiceSelect,
    handleCustomAnswerChange,
    handleChoiceConfirm,
    handleChoiceCancel,
    ...confirmations,
  };
}

/**
 * The confirmation half: the card this thread should be showing, how its
 * answer is going, and the ways to be done with it. One turn raises a card per
 * write-tool call, so the view renders how many are still waiting.
 */
function useToolConfirmations(threadId: string) {
  const chatStore = useChatStore();

  // The question id comes back from the card that was clicked: the store
  // answers that question or nothing, so a head that moved between render and
  // click cannot take the user's approval onto a different write.
  const handleToolConfirmationApprove = useCallback(
    (questionId: string, options?: { remember?: boolean; overrides?: Record<string, unknown> }) =>
      chatStore.submitToolConfirmation(
        threadId,
        questionId,
        true,
        options?.remember ?? false,
        options?.overrides
      ),
    [chatStore, threadId]
  );

  const handleToolConfirmationDeny = useCallback(
    (questionId: string) => chatStore.submitToolConfirmation(threadId, questionId, false),
    [chatStore, threadId]
  );

  const handleToolConfirmationDismiss = useCallback(
    (questionId: string) => chatStore.dismissToolConfirmation(threadId, questionId),
    [chatStore, threadId]
  );

  return {
    toolConfirmation: chatStore.getToolConfirmation(threadId),
    toolConfirmationSubmission: chatStore.getToolConfirmationSubmission(threadId),
    handleToolConfirmationApprove,
    handleToolConfirmationDeny,
    handleToolConfirmationDismiss,
  };
}
